import { Chip } from "@heroui/react"
import { User } from "@heroui/user"
import React, { Key, useCallback, useMemo } from "react"

import Phase from "../Form/Phase"

import { Animal } from "./Columns"

import { AuthStore } from "@/store/useAuthStore.ts"
import { calculateAge } from "@/lib/utils.ts"

import "../../styles/globals.css"
import FarmerUpdateAnimalWrapper from "@/features/Animals/Farmer-Update-Animal.tsx"
import PdfDownloadButton from "./PdfDownloadButton"

const formatMetric = (value: unknown): string => {
	if (value === null || value === undefined || value === "") return "-"
	const numericValue = Number(value)
	return Number.isFinite(numericValue) ? numericValue.toFixed(2) : String(value)
}

interface RenderCellProps {
	data: any
	columnKey: Key
}

const RenderCell: React.FC<RenderCellProps> = ({ data, columnKey }) => {
	const { farmInfo } = AuthStore()
	const maternalPhaseData = data.phases.find((entry: any) => entry.phaseI)?.phaseI
	const weaningPhaseData = data.phases.find((entry: any) => entry.phaseII)?.phaseII
	const yearlingPhaseData = data.phases.find((entry: any) => entry.phaseIII)?.phaseIII

	const renderCell = useCallback(
		(data: Animal, columnKey: Key) => {
			const cellValue = data[columnKey as keyof Animal]

			switch (columnKey) {
				case "adg":
				case "ww":
				case "yw":
				case "w120":
				case "pmwg":
				case "mw":
					return formatMetric(cellValue)
				case "profile":
					return (
						<>
							{data.image ? (
								<User
									avatarProps={{
										size: "md",
										radius: "full",
										src: `${data?.image}`,
										isBordered: true,
										name: `${data.name}`,
									}}
									description={data.gn.slice(0, 5)}
									name={<span className={""}>{data.name.slice(0, 6)}</span>}
								/>
							) : (
								<User
									avatarProps={{
										size: "md",
										radius: "full",
										isBordered: true,
										name: `${data.name}`,
									}}
									description={data.gn.slice(0, 5)}
									name={<span>{data.name.slice(0, 6)}</span>}
								/>
							)}
						</>
					)
				case "dateWeightI":
					return <span>{maternalPhaseData?.dateWeightI}</span>
				case "weightI":
					return <span>{maternalPhaseData?.weightI}</span>
				case "waterIntake":
					return <span>{maternalPhaseData?.waterIntake}</span>
				case "feedingSchedule":
					return <span>{maternalPhaseData?.feedingSchedule}</span>
				case "foodPreference":
					return <span>{maternalPhaseData?.foodPreference}</span>
				case "dietType":
					return <span>{maternalPhaseData?.dietType}</span>
				case "dr2":
					return <span>{weaningPhaseData?.dr2}</span>
				case "dateWeight2":
					return <span>{weaningPhaseData?.dateWeight2}</span>
				case "weight2":
					return <span>{weaningPhaseData?.weight2}</span>
				case "cbc2":
					return <span>{weaningPhaseData?.cbc2}</span>
				case "dateSp2":
					return <span>{weaningPhaseData?.dateSp2}</span>
				case "sp2":
					return <span>{weaningPhaseData?.sp2}</span>
				case "drSp2":
					return <span>{weaningPhaseData?.drSp2}</span>
				case "dateAc2":
					return <span>{weaningPhaseData?.dateAc2}</span>
				case "ac2":
					return <span>{weaningPhaseData?.ac2}</span>
				case "drAc2":
					return <span>{weaningPhaseData?.drAc2}</span>
				case "dateUs2":
					return <span>{weaningPhaseData?.dateUs2}</span>
				case "drUs2":
					return <span>{weaningPhaseData?.drUs2}</span>
				case "rea2":
					return <span>{weaningPhaseData?.rea2}</span>
				case "sft2":
					return <span>{weaningPhaseData?.sft2}</span>
				case "mar2":
					return <span>{weaningPhaseData?.mar2}</span>
				case "dateWeight3":
					return <span>{yearlingPhaseData?.dateWeight3}</span>
				case "weight3":
					return <span>{yearlingPhaseData?.weight3}</span>
				case "dr3":
					return <span>{yearlingPhaseData?.dr3}</span>
				case "dateSp3":
					return <span>{yearlingPhaseData?.dateSp3}</span>
				case "sp3":
					return <span>{yearlingPhaseData?.sp3}</span>
				case "drSp3":
					return <span>{yearlingPhaseData?.drSp3}</span>
				case "dateAc3":
					return <span>{yearlingPhaseData?.dateAc3}</span>
				case "ac3":
					return <span>{yearlingPhaseData?.ac3}</span>
				case "drAc3":
					return <span>{yearlingPhaseData?.drAc3}</span>
				case "dateUs3":
					return <span>{yearlingPhaseData?.dateUs3}</span>
				case "drUs3":
					return <span>{yearlingPhaseData?.drUs3}</span>
				case "rea3":
					return <span>{yearlingPhaseData?.rea3}</span>
				case "sft3":
					return <span>{yearlingPhaseData?.sft3}</span>

				case "birthDate": {
					const age = calculateAge(new Date(data.birthDate))

					return (
						<div className='flex flex-col'>
							<p className='text-bold text-small capitalize'>{data.birthDate}</p>
							<p className='text-bold text-tiny capitalize text-default-400'>Age:{age.months} mon </p>
						</div>
					)
				}
				case "farmer":
					return (
						<div className='flex flex-col'>
							<p className='text-bold text-small capitalize'>{farmInfo.farmName}</p>
							<p className='text-bold text-tiny capitalize text-default-400'>{farmInfo.id}</p>
						</div>
					)
				case "weight":
					return (
						<div className='flex flex-col'>
							<p className='text-bold text-small capitalize'>
								{data.weight}
								{data.unit}
							</p>
						</div>
					)
				case "address":
					return (
						<div className='flex flex-col'>
							<p className='text-bold text-small capitalize'>{farmInfo.address}</p>
						</div>
					)
				case "phases":
					return (
						<div className='flex flex-col'>
							<div className='text-bold text-small capitalize'>
								{(data.phases.length === 0 && (
									<Chip
										color='default'
										variant='flat'>
										Initial
									</Chip>
								)) ||
									(data.phases.length === 1 && (
										<Chip
											color='primary'
											variant='flat'>
											Maternal Phase
										</Chip>
									)) ||
									(data.phases.length === 2 && (
										<Chip
											color='warning'
											variant='flat'>
											Weaning Phase
										</Chip>
									)) ||
									(data.phases.length === 3 && (
										<Chip
											color='success'
											variant='flat'>
											Yearning Phase
										</Chip>
									))}
							</div>
						</div>
					)
				case "addPhase":
					return (
						<div>
							{!data.phaseIStatus && !data.phaseIIStatus && !data.phaseIIIStatus && (
								<Chip>Maternal Phase</Chip>
							)}
							{data.phaseIStatus && !data.phaseIIStatus && !data.phaseIIIStatus && (
								<Chip>Weaning Phase</Chip>
							)}
							{data.phaseIStatus && data.phaseIIStatus && !data.phaseIIIStatus && (
								<Chip>Yearling Phase</Chip>
							)}
						</div>
					)
				case "maternalPhase":
					return (
						// <Suspense fallback={<div >Loading...</div >} >
						// </Suspense >
						<Phase
							animal={data}
							disabled={false}
							id={data._id}
							name={"Add Maternal Phase "}
							phaseStage={data.phases}
						/>
					)
				case "weaningPhase":
					return (
						// <Suspense fallback={<div >Loading...</div >} >
						// </Suspense >
						<Phase
							animal={data}
							disabled={false}
							id={data._id}
							name={"Add Weaning Phase "}
							phaseStage={data.phases}
						/>
					)
				case "yearlingPhase":
					return (
						// <Suspense fallback={<div >Loading...</div >} >
						// </Suspense >
						<Phase
							animal={data}
							disabled={false}
							id={data._id}
							name={"Add Yearling Phase "}
							phaseStage={data.phases}
						/>
					)
				case "animalStatus":
					return (
						<Chip
							className='capitalize'
							color={
								data.animalStatus === "unknown"
									? "danger"
									: data.animalStatus === "active"
										? "primary"
										: data.animalStatus === "inactive"
											? "warning"
											: data.animalStatus === "semen" || data.animalStatus === "embryo"
												? "success"
												: undefined
							}
							size='sm'
							variant='flat'>
							{data.animalStatus}
						</Chip>
					)
				case "actions":
					return (
						<div className='flex items-center gap-2'>
							<FarmerUpdateAnimalWrapper data={data} />
							<PdfDownloadButton
								animalId={data._id}
								gn={data.gn}
							/>
						</div>
					)
				default:
					return cellValue
			}
		},
		[
			farmInfo.address,
			farmInfo.farmName,
			farmInfo.id,
			maternalPhaseData?.dateWeightI,
			maternalPhaseData?.dietType,
			maternalPhaseData?.feedingSchedule,
			maternalPhaseData?.foodPreference,
			maternalPhaseData?.waterIntake,
			maternalPhaseData?.weightI,
			weaningPhaseData?.ac2,
			weaningPhaseData?.cbc2,
			weaningPhaseData?.dateAc2,
			weaningPhaseData?.dateSp2,
			weaningPhaseData?.dateUs2,
			weaningPhaseData?.dateWeight2,
			weaningPhaseData?.dr2,
			weaningPhaseData?.drAc2,
			weaningPhaseData?.drSp2,
			weaningPhaseData?.drUs2,
			weaningPhaseData?.mar2,
			weaningPhaseData?.rea2,
			weaningPhaseData?.sft2,
			weaningPhaseData?.sp2,
			weaningPhaseData?.weight2,
			yearlingPhaseData?.ac3,
			yearlingPhaseData?.dateAc3,
			yearlingPhaseData?.dateSp3,
			yearlingPhaseData?.dateUs3,
			yearlingPhaseData?.dateWeight3,
			yearlingPhaseData?.dr3,
			yearlingPhaseData?.drAc3,
			yearlingPhaseData?.drSp3,
			yearlingPhaseData?.drUs3,
			yearlingPhaseData?.rea3,
			yearlingPhaseData?.sft3,
			yearlingPhaseData?.sp3,
			yearlingPhaseData?.weight3,
		]
	)

	return useMemo(() => renderCell(data, columnKey), [renderCell, data, columnKey])
}

export default RenderCell
